import Animated, { FadeIn } from 'react-native-reanimated';
import { StyleSheet, Text, View } from 'react-native';

import { getPerson } from '@/game/people';
import { ROLES, type Roster } from '@/game/types';
import { colors, radius, type } from '@/theme';

type Props = {
  roster: Roster;
  /** Slot to flash gold — the one that was just filled. */
  highlight?: string | null;
};

/**
 * The compact team, always on screen during the draft. Six slots, three up.
 */
export function RosterStrip({ roster, highlight = null }: Props) {
  return (
    <View style={styles.grid}>
      {ROLES.map((role) => {
        const pick = roster[role];
        const isNew = highlight === role;
        return (
          <Animated.View
            key={`${role}:${pick?.personId ?? 'empty'}`}
            entering={pick ? FadeIn.duration(260) : undefined}
            style={[styles.slot, pick ? styles.slotFilled : styles.slotEmpty, isNew && styles.slotNew]}>
            <Text style={[styles.role, isNew && styles.roleNew]} numberOfLines={1}>
              {role}
            </Text>
            <Text
              style={[styles.name, !pick && styles.nameEmpty, isNew && styles.nameNew]}
              numberOfLines={1}>
              {pick ? getPerson(pick.personId).name : 'Empty'}
            </Text>
          </Animated.View>
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  slot: {
    flexGrow: 1,
    flexBasis: '30%',
    minWidth: 96,
    borderRadius: radius.md,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderWidth: 1,
  },
  slotFilled: {
    backgroundColor: colors.card,
    borderColor: colors.border,
  },
  slotEmpty: {
    backgroundColor: 'transparent',
    borderColor: colors.border,
    borderStyle: 'dashed',
  },
  slotNew: {
    borderColor: colors.gold,
    backgroundColor: 'rgba(242, 203, 78, 0.08)',
  },
  role: {
    ...type.label,
    fontSize: 9,
    color: colors.faint,
    marginBottom: 3,
  },
  roleNew: {
    color: colors.gold,
  },
  name: {
    ...type.body,
    fontWeight: '700',
    fontSize: 13,
    color: colors.text,
  },
  nameEmpty: {
    color: colors.faint,
    fontWeight: '500',
  },
  nameNew: {
    color: colors.gold,
  },
});
